import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { initFlowbite } from 'flowbite';
import { filter, take } from 'rxjs/operators';
import { InactivityService } from './core/services/inactivity/inactivity.service';
import { AuthService } from './core/services/auth/auth.service';
import {
    applyLegacyExperienceDom,
    applyLegacyExperienceDomFromLocalStorage,
} from './core/utils/legacy-experience-appearance';
import { persistWorkspaceMemberPreferences } from './core/utils/workspace-member-format.util';
import { SettingsUsersService } from './pages/settings/users/settings-users.service';
import { BrowserCallControlsComponent } from '@app-shared/crm-record-components/browser-call-controls/browser-call-controls.component';
import { BrowserCallService } from '@app/modules/crm/services/browser-call.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, BrowserCallControlsComponent],
    template: '<router-outlet></router-outlet><app-browser-call-controls />'
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly inactivityService = inject(InactivityService);
    private readonly router = inject(Router);
    private readonly browserCallService = inject(BrowserCallService);
    private readonly auth = inject(AuthService);
    private readonly membersApi = inject(SettingsUsersService);
    private readonly store = inject(Store<any>);
    private onAuthRoute = false;
    private osSchemeMql?: MediaQueryList;
    private readonly onOsSchemeChange = (): void => {
        applyLegacyExperienceDomFromLocalStorage();
    };

    ngOnInit(): void {
        initFlowbite();
        applyLegacyExperienceDomFromLocalStorage();
        if (typeof window !== 'undefined' && window.matchMedia) {
            this.osSchemeMql = window.matchMedia('(prefers-color-scheme: dark)');
            this.osSchemeMql.addEventListener('change', this.onOsSchemeChange);
        }
        const initial = this.normalizePath(this.router.url);
        this.onAuthRoute = initial.startsWith('/auth');
        if (!this.onAuthRoute && this.auth.isLoggedIn()) {
            this.syncAppearanceFromServer();
        }
        if (!this.onAuthRoute) {
            this.inactivityService.startTracking();
            void this.browserCallService.ensureAgentDevicesRegistered();
        }
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => {
                const path = this.normalizePath(e.urlAfterRedirects);
                const nextAuth = path.startsWith('/auth') || path.startsWith('/onboarding');
                if (nextAuth && !this.onAuthRoute) {
                    this.inactivityService.stopTracking();
                } else if (!nextAuth && this.onAuthRoute) {
                    this.inactivityService.restartTracking();
                    void this.browserCallService.ensureAgentDevicesRegistered();
                    if (this.auth.isLoggedIn()) {
                        this.syncAppearanceFromServer();
                    }
                }
                this.onAuthRoute = nextAuth;
            });
    }

    private syncAppearanceFromServer(): void {
        const userId =
            typeof localStorage !== 'undefined'
                ? localStorage.getItem('id')?.trim()
                : '';
        if (!userId || !this.auth.isLoggedIn()) {
            return;
        }
        this.membersApi
            .getMemberByUserId(userId)
            .pipe(take(1))
            .subscribe((row) => {
                if (!row) {
                    return;
                }
                const accent = String(row.accentColor ?? 'violet').trim().toLowerCase();
                const theme = this.apiColorSchemeToTheme(row.colorScheme);
                const colorScheme = theme === 'dark' ? 'DARK' : theme === 'system' ? 'SYSTEM' : 'LIGHT';
                this.store.dispatch({ type: 'toggleTheme', payload: theme });
                applyLegacyExperienceDom(colorScheme, accent);
                persistWorkspaceMemberPreferences(row, {
                    colorScheme,
                    accentColor: accent,
                });
            });
    }

    private apiColorSchemeToTheme(
        cs: string | null | undefined,
    ): 'light' | 'dark' | 'system' {
        const u = String(cs ?? 'LIGHT').toUpperCase();
        if (u === 'DARK') return 'dark';
        if (u === 'SYSTEM') return 'system';
        return 'light';
    }

    private normalizePath(url: string): string {
        const path = (url.split('?')[0] ?? url).trim();
        return path.startsWith('/') ? path : `/${path}`;
    }

    ngOnDestroy(): void {
        this.osSchemeMql?.removeEventListener('change', this.onOsSchemeChange);
        this.osSchemeMql = undefined;
    }
}

